import type { PropTypes } from './LeaderboardList.types';
import type { Cancellable } from '../../../types';
import type { ReactElement } from 'react';

import { Card, Flex, Text } from '@rango-dev/whale';
import { Pagination } from '@rango-dev/whale/lab';
import { CircleDollarSign, CircleStar } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { usePaginationLabels } from '../../../hooks/usePaginationLabels';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { fetchPublicLeaderboard, fetchTopPublicLeaderboard } from '../../../state/ProfileSlice';
import { HoverCard } from '../../Profile/HoverCard';
import { List } from '../../Profile/Leaderboard/List';
import { displayUsdValue } from '../../Profile/ProfileSummary/ProfileSummary.helpers';
import {
  CompactTopUserCard,
  CompactTopUserCardSkeleton,
  TopUserCard,
  TopUserCardSkeleton,
} from '../TopUserCard';

import styles from './LeaderboardList.module.css';

const PAGE_SIZE = 20;
const TOP_USER_COUNT = 3;
const FIRST_PAGE_SKELETON_ROWS = 17;
const NEXT_PAGE_SKELETON_ROWS = 20;
const SCORE_ICON_SIZE = 20;

export function LeaderboardList(props: PropTypes): ReactElement {
  const { type } = props;
  const { t } = useTranslation();
  const { isMobile, isTablet } = useWindowDimensions();
  const isWideScreen = useMediaQuery('(min-width: 1280px)');
  const isSmall = isMobile || isTablet;
  const paginationLabels = usePaginationLabels();

  const dispatch = useAppDispatch();
  const { data: profileDetails } = useAppSelector((state) => state.profile.profileDetails);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParams = Number(searchParams.get('page'));
  const page = pageParams ? pageParams - 1 : 0;

  const {
    data: leaderboard,
    loading,
    totalPages,
  } = useAppSelector((state) => state.profile.publicLeaderboard);
  const { data: topLeaderboard, loading: topLoading } = useAppSelector(
    (state) => state.profile.topPublicLeaderboard,
  );

  const topUsers = topLeaderboard?.slice(0, TOP_USER_COUNT);
  const otherUsers = page === 0 ? leaderboard?.slice(TOP_USER_COUNT) : leaderboard;

  const fetchPublicLeaderboardPromise = useRef<Cancellable | null>(null);
  const fetchTopPublicLeaderboardPromise = useRef<Cancellable | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);

  const leaderboardType = type === 'referral' ? 'REFERRAL' : 'SCORE';
  const scoreHeader =
    type === 'referral' ? (isMobile ? t('Volume') : t('Referral volume')) : t('Score');
  const scoreTooltipText =
    type === 'referral'
      ? t("The swap volume generated by the user's invited friends")
      : t('Your Rank will be updated every hour');

  const skeletonRows = page === 0 ? FIRST_PAGE_SKELETON_ROWS : NEXT_PAGE_SKELETON_ROWS;

  const listProps: React.ComponentProps<typeof List> =
    !otherUsers || loading
      ? {
          loading: true,
          maxLengthSkeleton: skeletonRows,
        }
      : {
          loading: false,
          list: otherUsers,
          userId: profileDetails?.id,
          showCurrentUserTag: true,
          itemClassName: 'leaderboard-item',
          formatScore:
            type === 'referral' ? (item) => displayUsdValue(item.referralVolume) : undefined,
          scoreIcon:
            type === 'score' ? (
              <CircleStar size={SCORE_ICON_SIZE} />
            ) : (
              <CircleDollarSign size={SCORE_ICON_SIZE} />
            ),
        };

  useEffect(() => {
    fetchPublicLeaderboardPromise.current = dispatch(
      fetchPublicLeaderboard({ page, offset: PAGE_SIZE, type: leaderboardType }),
    );
  }, [page, type]);

  useEffect(() => {
    if (page !== 0) {
      fetchTopPublicLeaderboardPromise.current = dispatch(
        fetchTopPublicLeaderboard({ type: leaderboardType }),
      );
    }
  }, [type]);

  const handlePageNavigation = (pageNumber: number) => {
    setSearchParams({ page: (pageNumber + 1).toString() });
  };

  /*
   * Above 1280px gets the full Set with the jump-to-page field; at or below
   * 1280px the compact Root (nav controls only), centered, keeps the footer tidy.
   */
  const renderPagination = (): ReactElement => {
    if (!totalPages) {
      return <Pagination.Skeleton />;
    }
    if (isWideScreen) {
      return (
        <Pagination.Set
          showEdgePages={!isMobile}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageNavigation}
          scrollTargetRef={listContainerRef}
          labels={paginationLabels}
        />
      );
    }
    return (
      <Flex justify="center" width="100%">
        <Pagination.Root
          showEdgePages={!isMobile}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageNavigation}
          labels={paginationLabels}
        />
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap={{ initial: '4', md: '5' }} flexGrow="1" minWidth="0">
      <Flex direction="row" gap={{ initial: '2', md: '5' }} align="stretch" width="100%">
        {!topUsers || topLoading
          ? Array.from({ length: TOP_USER_COUNT }, (_, index) =>
              isSmall ? (
                <CompactTopUserCardSkeleton key={index} />
              ) : (
                <TopUserCardSkeleton key={index} rank={index + 1} />
              ),
            )
          : topUsers.map((user) =>
              isSmall ? (
                <CompactTopUserCard key={user.id} user={user} type={type} />
              ) : (
                <TopUserCard key={user.id} user={user} type={type} />
              ),
            )}
      </Flex>

      <Card
        ref={listContainerRef}
        variant="surface"
        size={{ initial: '2', md: '3' }}
        className={styles.scrollTarget}>
        <Flex direction="column" gap="3" width="100%">
          <Flex justify="between" align="center" width="100%">
            <Flex gap={{ initial: '2', sm: '4' }} align="center" pl="2">
              <Flex width="48px" align="start">
                <Text size="2" weight="bold">
                  {t('Rank')}
                </Text>
              </Flex>
              <Text size="2" weight="bold">
                {t('User')}
              </Text>
            </Flex>
            <Flex align="center" gap="1" mr="2">
              <Text size="2" weight="bold">
                {scoreHeader}
              </Text>
              <HoverCard iconSize={16} description={scoreTooltipText} />
            </Flex>
          </Flex>
          <List showLevel={false} avatarSize={{ initial: '3', md: '4' }} {...listProps} />
          {renderPagination()}
        </Flex>
      </Card>
    </Flex>
  );
}
